import { Box, Typography } from '@mui/material'
import { ListingPage } from 'components/ListingPage/ListingPage'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'
import { Column } from 'components/Table/Table'
import React, { Dispatch, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { SearchProduct } from 'types/api/product'
import { ProductImage } from 'views/Products/ProductImage'
import { NameColumn } from './NameColumn'
import { ConfigFieldTypes } from './SearchConfig'

type Pagination = {
  page: number
  pageSize: number
  totalPages: number
  totalCount: number
  nextPage: number
}

type ProductTableProps = {
  productData: SearchProduct[]
  pagination: Pagination
  page: number
  setPage: Dispatch<SetStateAction<number>>
  submitForm: (values: ConfigFieldTypes) => void
  values: ConfigFieldTypes
  isLoading: boolean
}

export const ProductTable = ({
  productData,
  pagination,
  page,
  setPage,
  submitForm,
  values,
  isLoading,
}: ProductTableProps) => {
  const { t } = useTranslation()

  const columns: Column<
    | 'score'
    | 'keywords'
    | 'name'
    | 'image'
    | 'description'
    | 'functionalName'
    | 'brand'
    | 'gtin'
    | 'categories'
  >[] = [
    { id: 'score', label: t('score'), minWidth: 50, cellWidth: '50px' },
    { id: 'image', label: '', minWidth: 50, cellWidth: '50px' },
    { id: 'name', label: t('name').toUpperCase(), minWidth: 100 },
    { id: 'brand', label: t('brand'), minWidth: 100 },
    { id: 'keywords', label: t('keywords'), minWidth: 100 },
    { id: 'description', label: t('description'), minWidth: 80 },
    { id: 'functionalName', label: t('functionalName'), minWidth: 100 },
    { id: 'categories', label: t('categories'), minWidth: 100 },
    { id: 'gtin', label: t('GTIN'), minWidth: 100 },
  ]

  const getValue = (product: SearchProduct, id: string) => {
    const data = id.split('.').reduce((p, prop) => {
      return p[prop as keyof typeof product] as unknown as SearchProduct
    }, product)
    if (id === 'name') {
      return (
        <NameColumn
          productName={
            product.name
              ? product.name[
                  values.contentLanguage as keyof typeof product.name
                ]
              : ''
          }
          maxWidth='130px'
        />
      )
    }
    if (id === 'brand') {
      return product.brand
        ? product.brand[values.contentLanguage as keyof typeof product.brand]
        : ''
    }
    if (id === 'image') {
      const imgInfo =
        product.details.media.length > 0
          ? product.details.media[0].mediaStorageKey
          : ''
      return <ProductImage imgInfo={imgInfo} />
    }
    if (id === 'score') {
      return (
        <Box m={1}>
          <Typography variant='body2'>{product.esScore}</Typography>
        </Box>
      )
    }
    if (id === 'description') {
      return (
        <NameColumn
          productName={
            product.description
              ? product.description[
                  values.contentLanguage as keyof typeof product.description
                ]
              : ''
          }
          maxWidth='130px'
        />
      )
    }
    if (id === 'functionalName') {
      return product.functionalName
        ? product.functionalName[
            values.contentLanguage as keyof typeof product.functionalName
          ]
        : ''
    }
    if (id === 'keywords') {
      return (
        <Box m={1}>
          <Typography>
            {product.keywords
              ? product.keywords
                  .map((keyword) =>
                    keyword
                      ? keyword[values.contentLanguage as keyof typeof keyword]
                      : ''
                  )
                  .join(', ')
              : ''}
          </Typography>
        </Box>
      )
    }
    if (id === 'categories') {
      const productCategories = product.categories.map((category) =>
        category.name
          ? category.name[values.contentLanguage as keyof typeof category.name]
          : ''
      )
      return (
        <Box m={1}>
          <Typography>
            {productCategories?.length > 0 ? productCategories.join(', ') : ''}
          </Typography>
        </Box>
      )
    }

    return data
  }

  const handleChangePage = (newPage: number) => {
    if (newPage > 0 && newPage <= pagination.totalPages) {
      setPage(newPage)
      submitForm(values)
    }
  }

  if (isLoading) return <LoadingIndicator />
  return (
    <Box>
      <ListingPage
        productTable
        tableWidth='100%'
        tableHeight='calc(100vh - 220px)'
        columns={columns}
        listItems={productData}
        pagination={pagination}
        page={page}
        handleChangePage={handleChangePage}
        displayValues={getValue}
      />
    </Box>
  )
}
